import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import type { MessageTemplate, Contact, PressList } from '@pf/shared';

// ── Merge field helpers ────────────────────────────────────────────────────────

const MERGE_FIELDS = [
  { label: 'First Name', token: '{{first_name}}' },
  { label: 'Last Name', token: '{{last_name}}' },
  { label: 'Company', token: '{{company}}' },
  { label: 'Publication', token: '{{publication}}' },
];

const AI_REPHRASE_MODES = [
  'Variety', 'Clever', 'Urgent', 'Emotional', 'Question',
  'FOMO', 'Benefit-Driven', 'Teaser', 'Mystery', 'Listicle', 'Unhinged',
];

// Scan text for {{token}} usage and return unique field names
function extractMergeFields(text: string): string[] {
  const matches = [...text.matchAll(/\{\{(\w+)\}\}/g)];
  return [...new Set(matches.map((m) => m[1]))];
}

// ── Rich text editor ───────────────────────────────────────────────────────────

interface RichEditorProps {
  initialHtml: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

function RichEditor({ initialHtml, onChange, placeholder }: RichEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !initialized.current) {
      editorRef.current.innerHTML = initialHtml;
      initialized.current = true;
    }
  }, [initialHtml]);

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML ?? '');
  }, [onChange]);

  function execCmd(cmd: string, value?: string) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    handleInput();
  }

  function insertText(text: string) {
    editorRef.current?.focus();
    document.execCommand('insertText', false, text);
    handleInput();
  }

  function insertLink() {
    const url = prompt('Enter URL:');
    if (url) execCmd('createLink', url);
  }

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
      {/* Formatting toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCmd('bold'); }}
          className="px-2 py-1 text-xs font-bold text-gray-600 hover:bg-gray-200 rounded"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCmd('italic'); }}
          className="px-2 py-1 text-xs italic text-gray-600 hover:bg-gray-200 rounded"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCmd('underline'); }}
          className="px-2 py-1 text-xs underline text-gray-600 hover:bg-gray-200 rounded"
          title="Underline"
        >
          U
        </button>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); insertLink(); }}
          className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
          title="Insert link"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {/* Merge field insertion */}
        {MERGE_FIELDS.map((f) => (
          <button
            key={f.token}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); insertText(f.token); }}
            className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded font-mono"
            title={`Insert ${f.label} merge field`}
          >
            {f.token}
          </button>
        ))}
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder ?? 'Write your email…'}
        className="min-h-[240px] p-4 text-sm text-gray-900 focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
      />
    </div>
  );
}

// ── Template picker ────────────────────────────────────────────────────────────

interface TemplateSelectorProps {
  templates: MessageTemplate[];
  onLoad: (t: MessageTemplate) => void;
}

function TemplateSelector({ templates, onLoad }: TemplateSelectorProps) {
  const [selected, setSelected] = useState('');

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = e.target.value;
    setSelected(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) onLoad(tpl);
  }

  return (
    <select
      value={selected}
      onChange={handleChange}
      className="block rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="">Load template…</option>
      {templates.map((t) => (
        <option key={t.id} value={t.id}>{t.name}</option>
      ))}
    </select>
  );
}

// ── Contact picker (for preview) ───────────────────────────────────────────────

interface ContactPickerProps {
  contacts: Contact[];
  value: string;
  onChange: (id: string) => void;
}

function ContactPicker({ contacts, value, onChange }: ContactPickerProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
    >
      <option value="">Select contact for preview…</option>
      {contacts.map((c) => (
        <option key={c.id} value={c.id}>
          {c.firstName} {c.lastName}{c.company ? ` — ${c.company}` : ''}
        </option>
      ))}
    </select>
  );
}

// ── AI subject suggestions ─────────────────────────────────────────────────────

interface AiSubjectSuggestionsProps {
  bodyHtml: string;
  pinnedContact: Contact | null;
  onSelect: (subject: string) => void;
}

function AiSubjectSuggestions({ bodyHtml, pinnedContact, onSelect }: AiSubjectSuggestionsProps) {
  const [open, setOpen] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [rawText, setRawText] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const canSuggest = !!bodyHtml.replace(/<[^>]+>/g, '').trim();

  function parseSubjectLines(text: string): string[] {
    return text
      .split('\n')
      .map((line) => line.replace(/^\d+\.\s*/, '').trim())
      .filter(Boolean);
  }

  async function handleFetchSuggestions() {
    if (!canSuggest) return;
    if (streaming) {
      abortRef.current?.abort();
      return;
    }

    setOpen(true);
    setRawText('');
    setSuggestions([]);
    setError(null);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const publicationName = pinnedContact?.company ?? undefined;
    const reporterBeats =
      pinnedContact?.reporter?.beats?.length
        ? pinnedContact.reporter.beats.join(', ')
        : undefined;

    try {
      const token = localStorage.getItem('pf_token');
      const resp = await fetch('/api/messages/suggest-subject', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ bodyHtml, publicationName, reporterBeats }),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          let event: { type: string; value?: string; message?: string };
          try {
            event = JSON.parse(raw);
          } catch {
            continue;
          }

          if (event.type === 'text' && event.value) {
            accumulated += event.value;
            setRawText(accumulated);
          } else if (event.type === 'error') {
            setError(event.message ?? 'AI suggestion failed.');
            break;
          } else if (event.type === 'done') {
            setSuggestions(parseSubjectLines(accumulated));
          }
        }
      }
    } catch (err) {
      if ((err as { name?: string }).name !== 'AbortError') {
        setError('Connection error. Please try again.');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  function handleSelect(s: string) {
    onSelect(s);
    setOpen(false);
  }

  function handleDismiss() {
    setOpen(false);
    setRawText('');
    setSuggestions([]);
    setError(null);
    abortRef.current?.abort();
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleFetchSuggestions}
        disabled={!canSuggest}
        className={[
          'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
          canSuggest
            ? 'border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 cursor-pointer'
            : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed',
        ].join(' ')}
        title={canSuggest ? 'AI subject line suggestions' : 'Add a body first to get suggestions'}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        {streaming ? 'Generating…' : 'Suggest subject lines'}
      </button>

      {open && (
        <div className="mt-2 rounded-md border border-indigo-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-indigo-50 border-b border-indigo-100">
            <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">AI Suggestions</span>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Dismiss
            </button>
          </div>

          {error && (
            <div className="px-3 py-2 text-xs text-red-600 bg-red-50">
              {error}
            </div>
          )}

          {!error && suggestions.length === 0 && streaming && (
            <div className="px-3 py-3 text-xs text-gray-400 animate-pulse">
              {rawText || 'Generating suggestions…'}
            </div>
          )}

          {suggestions.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {suggestions.map((s, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => handleSelect(s)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50 transition-colors"
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── AI rephrase toolbar ────────────────────────────────────────────────────────

const REPHRASE_CACHE_KEY = 'pf_rephrase_last';

interface RephraseCache {
  templateId: string;
  mode: string;
  rawText: string;
}

interface AiRephraseToolbarProps {
  templateId: string | null;
  subject: string;
  bodyHtml: string;
  onAdopt: (subject: string, bodyHtml: string) => void;
}

function AiRephraseToolbar({ templateId, subject, bodyHtml, onAdopt }: AiRephraseToolbarProps) {
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [rawText, setRawText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Load cached result from localStorage on mount
  useEffect(() => {
    if (!templateId) return;
    try {
      const stored = localStorage.getItem(REPHRASE_CACHE_KEY);
      if (stored) {
        const cache: RephraseCache = JSON.parse(stored);
        if (cache.templateId === templateId) {
          setActiveMode(cache.mode);
          setRawText(cache.rawText);
        }
      }
    } catch {
      // ignore
    }
  }, [templateId]);

  async function handleModeClick(mode: string) {
    if (!templateId) return;
    if (streaming) {
      abortRef.current?.abort();
      return;
    }

    setActiveMode(mode);
    setRawText('');
    setError(null);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem('pf_token');
      const resp = await fetch(`/api/message-templates/${templateId}/rephrase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mode, subject, bodyHtml }),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          let event: { type: string; value?: string; message?: string };
          try {
            event = JSON.parse(raw);
          } catch {
            continue;
          }

          if (event.type === 'text' && event.value) {
            accumulated += event.value;
            setRawText(accumulated);
          } else if (event.type === 'error') {
            setError(event.message ?? 'AI rephrasing failed.');
            break;
          } else if (event.type === 'done') {
            // Save to localStorage cache
            try {
              const cache: RephraseCache = { templateId, mode, rawText: accumulated };
              localStorage.setItem(REPHRASE_CACHE_KEY, JSON.stringify(cache));
            } catch {
              // ignore storage errors
            }
          }
        }
      }
    } catch (err) {
      if ((err as { name?: string }).name !== 'AbortError') {
        setError('Connection error. Please try again.');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  // Parse rawText into subject + body (format: "SUBJECT: ...\n\n<body>")
  function parseSuggestion(text: string): { subject: string; body: string } {
    const lines = text.split('\n');
    let parsedSubject = '';
    let bodyStart = 0;

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith('SUBJECT:')) {
        parsedSubject = lines[i].slice('SUBJECT:'.length).trim();
        bodyStart = i + 1;
        // Skip leading blank lines after SUBJECT
        while (bodyStart < lines.length && !lines[bodyStart].trim()) bodyStart++;
        break;
      }
    }

    const bodyLines = lines.slice(bodyStart);
    // Convert plain paragraphs to HTML
    const bodyHtml = bodyLines
      .join('\n')
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
      .join('');

    return { subject: parsedSubject, body: bodyHtml };
  }

  function handleAdopt() {
    if (!rawText) return;
    const { subject: newSubject, body: newBody } = parseSuggestion(rawText);
    onAdopt(newSubject || subject, newBody || bodyHtml);
  }

  function handleDismiss() {
    setRawText('');
    setActiveMode(null);
    setError(null);
    localStorage.removeItem(REPHRASE_CACHE_KEY);
  }

  const canRephrase = !!templateId && !!bodyHtml.replace(/<[^>]+>/g, '').trim();

  return (
    <div className="rounded-lg border border-purple-100 bg-purple-50/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">AI Rephrase</span>
        {!templateId && (
          <span className="text-xs text-gray-400 ml-1">— save as template to enable</span>
        )}
        {streaming && (
          <span className="text-xs text-purple-600 ml-1 animate-pulse">Generating…</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {AI_REPHRASE_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => handleModeClick(mode)}
            disabled={!canRephrase}
            className={[
              'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
              activeMode === mode
                ? 'border-purple-500 bg-purple-100 text-purple-800'
                : 'border-purple-200 text-purple-700 bg-white hover:bg-purple-50',
              !canRephrase ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            {mode}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {rawText && !error && (
        <div className="mt-3 rounded-md border border-purple-200 bg-white overflow-hidden">
          <div className="px-3 py-2 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
            <span className="text-xs font-medium text-purple-700">{activeMode} suggestion</span>
            <div className="flex items-center gap-2">
              {!streaming && (
                <button
                  type="button"
                  onClick={handleAdopt}
                  className="px-3 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
                >
                  Use this
                </button>
              )}
              <button
                type="button"
                onClick={handleDismiss}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Dismiss
              </button>
            </div>
          </div>
          <pre className="p-3 text-xs text-gray-800 whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto">
            {rawText}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Main compose page ──────────────────────────────────────────────────────────

export default function ComposePage() {
  const [searchParams] = useSearchParams();
  const contactIdParam = searchParams.get('contactId');
  const pressListIdParam = searchParams.get('pressListId');

  const queryClient = useQueryClient();

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewContactId, setPreviewContactId] = useState(contactIdParam ?? '');
  const [previewResult, setPreviewResult] = useState<{ subject: string | null; bodyHtml: string | null } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Editor key — change to re-mount editor when loading a template or adopting a rephrase
  const [editorKey, setEditorKey] = useState(0);
  const editorInitHtml = useRef('');

  // Fetch templates
  const { data: templatesData } = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => apiGet<{ messageTemplates: MessageTemplate[] }>('/message-templates'),
  });
  const templates = templatesData?.messageTemplates ?? [];

  // Fetch contacts for preview picker
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', { limit: 100 }],
    queryFn: () => apiGet<{ contacts: Contact[]; total: number }>('/contacts', { limit: 100 }),
  });
  const allContacts = contactsData?.contacts ?? [];

  // Fetch single contact if contactId param is set
  const { data: contactData } = useQuery({
    queryKey: ['contact', contactIdParam],
    queryFn: () => apiGet<{ contact: Contact }>(`/contacts/${contactIdParam}`),
    enabled: !!contactIdParam,
  });
  const pinnedContact = contactData?.contact ?? null;

  // Fetch press list if pressListId param is set
  const { data: pressListData } = useQuery({
    queryKey: ['press-list', pressListIdParam],
    queryFn: () => apiGet<{ pressList: PressList }>(`/press-lists/${pressListIdParam}`),
    enabled: !!pressListIdParam,
  });
  const pressList = pressListData?.pressList ?? null;

  // Save template mutation
  const saveTemplate = useMutation({
    mutationFn: (data: { name: string; subject: string; bodyHtml: string; mergeFields: string[] }) =>
      apiPost<{ messageTemplate: MessageTemplate }>('/message-templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-templates'] });
      setTemplateName('');
      setShowSaveTemplate(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    },
    onError: (e: Error) => setSaveError(e.message),
  });

  // Delete template mutation
  const deleteTemplate = useMutation({
    mutationFn: (id: string) => apiDelete(`/message-templates/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['message-templates'] }),
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: (payload: { subject: string; bodyHtml: string; contactId: string }) =>
      apiPost<{ subject: string | null; bodyHtml: string | null }>('/message-templates/preview', payload),
    onSuccess: (data) => setPreviewResult(data),
  });

  function loadTemplate(tpl: MessageTemplate) {
    setSubject(tpl.subject ?? '');
    editorInitHtml.current = tpl.bodyHtml ?? '';
    setBodyHtml(tpl.bodyHtml ?? '');
    setActiveTemplateId(tpl.id);
    setEditorKey((k) => k + 1);
  }

  function handleAdoptRephrase(newSubject: string, newBodyHtml: string) {
    setSubject(newSubject);
    editorInitHtml.current = newBodyHtml;
    setBodyHtml(newBodyHtml);
    setEditorKey((k) => k + 1);
  }

  function handleSaveTemplate(e: React.FormEvent) {
    e.preventDefault();
    if (!templateName.trim()) return;
    setSaveError(null);
    const mergeFields = extractMergeFields(`${subject} ${bodyHtml}`);
    saveTemplate.mutate({ name: templateName.trim(), subject, bodyHtml, mergeFields });
  }

  function handlePreview() {
    if (!previewContactId) return;
    previewMutation.mutate({ subject, bodyHtml, contactId: previewContactId });
    setPreviewMode(true);
  }

  function insertMergeFieldInSubject(token: string) {
    setSubject((s) => s + token);
  }

  // Context header
  function renderContextBanner() {
    if (pinnedContact) {
      return (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2">
          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>
            Composing for{' '}
            <Link to={`/contacts/${pinnedContact.id}`} className="font-medium text-blue-700 hover:underline">
              {pinnedContact.firstName} {pinnedContact.lastName}
            </Link>
            {pinnedContact.company && <span className="text-gray-500"> · {pinnedContact.company}</span>}
          </span>
        </div>
      );
    }
    if (pressList) {
      return (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 bg-green-50 border border-green-100 rounded-lg px-4 py-2">
          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span>
            Batch composing for list{' '}
            <Link to={`/press-lists/${pressList.id}`} className="font-medium text-green-700 hover:underline">
              {pressList.name}
            </Link>
            <span className="text-gray-500"> · {pressList.contactsCount} contacts</span>
          </span>
        </div>
      );
    }
    return null;
  }

  const activeDetectedFields = extractMergeFields(`${subject} ${bodyHtml}`);

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Compose</h1>
        <p className="mt-1 text-sm text-gray-500">
          Write your email, use merge fields for personalization, and save as a reusable template.
        </p>
      </div>

      {renderContextBanner()}

      {saveSuccess && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
          Template saved successfully.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Template picker */}
          <div className="flex items-center gap-3">
            <TemplateSelector templates={templates} onLoad={loadTemplate} />
            <button
              type="button"
              onClick={() => setShowSaveTemplate((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Save as template
            </button>
          </div>

          {/* Save template form */}
          {showSaveTemplate && (
            <form onSubmit={handleSaveTemplate} className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name…"
                required
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={saveTemplate.isPending}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saveTemplate.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setShowSaveTemplate(false); setSaveError(null); }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              {saveError && <span className="text-xs text-red-600">{saveError}</span>}
            </form>
          )}

          {/* Subject */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-700">Subject</label>
              <AiSubjectSuggestions
                bodyHtml={bodyHtml}
                pinnedContact={pinnedContact}
                onSelect={setSubject}
              />
            </div>
            <div className="flex gap-1 flex-wrap mb-1.5">
              {MERGE_FIELDS.map((f) => (
                <button
                  key={f.token}
                  type="button"
                  onClick={() => insertMergeFieldInSubject(f.token)}
                  className="px-2 py-0.5 text-xs font-mono text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                  title={`Insert ${f.label}`}
                >
                  {f.token}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject line…"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Body editor */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Body</label>
            <RichEditor
              key={editorKey}
              initialHtml={editorInitHtml.current}
              onChange={setBodyHtml}
              placeholder="Write your pitch here…"
            />
          </div>

          {/* Detected merge fields */}
          {activeDetectedFields.length > 0 && (
            <div className="text-xs text-gray-500">
              <span className="font-medium">Merge fields detected:</span>{' '}
              {activeDetectedFields.map((f) => (
                <span key={f} className="inline-flex items-center px-1.5 py-0.5 mr-1 rounded bg-blue-50 text-blue-700 font-mono">
                  {`{{${f}}}`}
                </span>
              ))}
            </div>
          )}

          {/* AI rephrase toolbar */}
          <AiRephraseToolbar
            templateId={activeTemplateId}
            subject={subject}
            bodyHtml={bodyHtml}
            onAdopt={handleAdoptRephrase}
          />

          {/* Preview controls */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Preview with merge fields</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <ContactPicker
                contacts={allContacts}
                value={previewContactId}
                onChange={setPreviewContactId}
              />
              <button
                type="button"
                onClick={handlePreview}
                disabled={!previewContactId || previewMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {previewMutation.isPending ? 'Loading…' : 'Preview'}
              </button>
              {previewMode && (
                <button
                  type="button"
                  onClick={() => { setPreviewMode(false); setPreviewResult(null); }}
                  className="text-sm text-gray-500 hover:text-gray-900"
                >
                  Close preview
                </button>
              )}
            </div>

            {previewMode && previewResult && (
              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Subject</p>
                  <p className="text-sm text-gray-900">
                    {previewResult.subject || <span className="text-gray-400 italic">No subject</span>}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Body</p>
                  {previewResult.bodyHtml ? (
                    <div
                      className="text-sm text-gray-900 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewResult.bodyHtml }}
                    />
                  ) : (
                    <p className="text-sm text-gray-400 italic">No body content</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar: saved templates */}
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Saved templates</h3>
            {templates.length === 0 ? (
              <p className="text-xs text-gray-400">No saved templates yet. Compose an email and click "Save as template."</p>
            ) : (
              <div className="space-y-2">
                {templates.map((t) => (
                  <div key={t.id} className="flex items-start justify-between gap-2 p-2 rounded-md hover:bg-gray-50">
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={() => loadTemplate(t)}
                        className="text-sm font-medium text-blue-600 hover:underline text-left truncate block w-full"
                      >
                        {t.name}
                      </button>
                      {t.subject && (
                        <p className="text-xs text-gray-500 truncate">{t.subject}</p>
                      )}
                      {t.mergeFields.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-1">
                          {t.mergeFields.slice(0, 3).map((f) => (
                            <span key={f} className="text-xs font-mono text-blue-600 bg-blue-50 px-1 rounded">
                              {`{{${f}}}`}
                            </span>
                          ))}
                          {t.mergeFields.length > 3 && (
                            <span className="text-xs text-gray-400">+{t.mergeFields.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteTemplate.mutate(t.id)}
                      disabled={deleteTemplate.isPending}
                      className="text-gray-300 hover:text-red-500 flex-shrink-0 mt-0.5"
                      title="Delete template"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Merge field reference */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Merge field reference</h3>
            <div className="space-y-1.5">
              {MERGE_FIELDS.map((f) => (
                <div key={f.token} className="flex items-center justify-between">
                  <span className="text-xs font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{f.token}</span>
                  <span className="text-xs text-gray-500">{f.label}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-400">
              These tokens are replaced with the contact's actual data when sending.
            </p>
          </div>

          {/* Quick links */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick links</h3>
            <div className="space-y-1">
              <Link
                to="/outreach"
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email account settings
              </Link>
              <Link
                to="/press-lists"
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Press lists
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
